/**
 * Impersonation state (WP-P6).
 *
 * Audit finding: `services/api.ts` has always sent `X-Account-Id` from
 * `localStorage.fleet_impersonated_account_id`, but nothing in the portal ever
 * set it — there was no UI. These helpers are that UI's state layer; the
 * banner lives in `ImpersonationBanner.tsx` and the control in `PlatformPage`.
 *
 * A full reload on enter/exit is deliberate: every cached query, the
 * WebSocket subscription and the feature flags belong to the impersonated
 * account, and a reload is the only way to be sure none of the old tenant's
 * data survives in memory.
 */
export const IMPERSONATION_ID_KEY = 'fleet_impersonated_account_id'
export const IMPERSONATION_NAME_KEY = 'fleet_impersonated_account_name'

export interface Impersonation { id: string; name: string }

export function getImpersonation(): Impersonation | null {
  try {
    const id = localStorage.getItem(IMPERSONATION_ID_KEY)
    if (!id) return null
    return { id, name: localStorage.getItem(IMPERSONATION_NAME_KEY) || id }
  } catch {
    return null
  }
}

/** Enter an account. Reloads so no cached tenant data leaks across. */
export function startImpersonation(account: Impersonation, reload = true): void {
  try {
    localStorage.setItem(IMPERSONATION_ID_KEY, account.id)
    localStorage.setItem(IMPERSONATION_NAME_KEY, account.name)
  } catch { /* storage disabled — the header simply won't be sent */ }
  if (reload) window.location.assign('/')
}

/** Leave the impersonated account and go back to the platform view. */
export function stopImpersonation(reload = true): void {
  try {
    localStorage.removeItem(IMPERSONATION_ID_KEY)
    localStorage.removeItem(IMPERSONATION_NAME_KEY)
  } catch { /* nothing to clear */ }
  if (reload) window.location.assign('/platform')
}
